import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "core-js/modules/es.string.link.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.array.join.js";
import React from 'react';
import { generatePath, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { slicePathSegments } from 'utils/ui';
import { getIsPlaylistEditable, hasPrivilege, PLAYLIST_WRITE } from 'utils/privileges';
import { useFetchPlaylistDetails } from 'actions/playlists';
import PageBackLink from 'components/PageBackLink';
import EditorPageHeader, { StickyEditorPageHeaderWrapper } from 'components/EditorPageHeader';
import FixedHeightTab from 'components/Tab/FixedHeightTab';
import Icon from 'components/Icon';
import useSearchParams from 'hooks/useSearchParams';
import createLocation from 'utils/createLocation';
import { HeaderOptionsMenu } from './HeaderOptionsMenu';
import PlaylistHeaderSubtitle from './PlaylistHeaderSubtitle';
export function SinglePlaylistManagerHeader(_ref) {
  var showHeaderBackLink = _ref.showHeaderBackLink,
    showAccessTab = _ref.showAccessTab,
    showContentTab = _ref.showContentTab,
    showShareTab = _ref.showShareTab,
    includeDeletedPlaylists = _ref.includeDeletedPlaylists,
    includeSearchParams = _ref.includeSearchParams,
    hasDeleteOrRestoreOption = _ref.hasDeleteOrRestoreOption,
    stickyNavbarHeight = _ref.stickyNavbarHeight,
    hasDuplicateOption = _ref.hasDuplicateOption,
    elementRef = _ref.elementRef,
    playlistId = _ref.playlistId,
    shareData = _ref.shareData,
    tabLinks = _ref.tabLinks,
    titleId = _ref.titleId,
    onDeletePlaylistSuccess = _ref.onDeletePlaylistSuccess,
    onClose = _ref.onClose;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var isPlaylistAdmin = hasPrivilege(PLAYLIST_WRITE);
  var _useSearchParams = useSearchParams(),
    _useSearchParams2 = _slicedToArray(_useSearchParams, 1),
    searchParams = _useSearchParams2[0];
  var _useFetchPlaylistDeta = useFetchPlaylistDetails({
      id: playlistId,
      includeDeleted: includeDeletedPlaylists
    }),
    playlist = _useFetchPlaylistDeta.data;
  var basePath = generatePath(tabLinks.details.link, {
    playlistId: playlistId
  });
  var playlistLink = slicePathSegments(basePath, 0, -1);
  var playlistName = (playlist === null || playlist === void 0 ? void 0 : playlist.name) || '';
  var isDeleted = playlist === null || playlist === void 0 ? void 0 : playlist.isDeleted;
  var isPlaylistEditable = playlist ? getIsPlaylistEditable({
    playlist: playlist,
    allowEditingDistributedPlaylists: true
  }) : undefined;

  // Ensure the tab is visible while the playlist is loading
  var hasDetailsTab = isPlaylistEditable !== false;

  // Hide the Access and the Share tab if the playlist is deleted
  var isAdminAndNotDeleted = isPlaylistAdmin && !isDeleted;
  var hasAccessTab = isAdminAndNotDeleted && showAccessTab;
  var hasShareTab = isAdminAndNotDeleted && showShareTab;
  var hasAnalyticsTab = isPlaylistAdmin;
  var hasContentTab = showContentTab && !isDeleted;
  var title = isDeleted ? t('playlists_heading_selection_includes_deleted', {
    count: 1,
    name: playlist === null || playlist === void 0 ? void 0 : playlist.name
  }) : (playlist === null || playlist === void 0 ? void 0 : playlist.name) || '';
  var getTabLink = function getTabLink(tab) {
    var commonPath = generatePath(tabLinks[tab].link, {
      playlistId: playlistId
    });
    if (includeSearchParams) {
      return createLocation(commonPath, searchParams);
    }
    return commonPath;
  };
  return /*#__PURE__*/React.createElement(StickyEditorPageHeaderWrapper, {
    distance: stickyNavbarHeight
  }, /*#__PURE__*/React.createElement(EditorPageHeader, {
    wrapperRef: elementRef,
    titleId: titleId,
    title: title,
    documentTitle: [t('actions_edit_playlist'), playlistName].join(' - '),
    navMenu: playlist && /*#__PURE__*/React.createElement(HeaderOptionsMenu, {
      playlist: playlist,
      shareData: shareData,
      onDeletePlaylistSuccess: onDeletePlaylistSuccess,
      hasDeleteOrRestoreOption: hasDeleteOrRestoreOption,
      hasDuplicateOption: hasDuplicateOption
    }),
    preTitleContent: showHeaderBackLink && /*#__PURE__*/React.createElement(PageBackLink, {
      to: playlistLink,
      mb: "xs"
    }, t('actions_back_to_playlist')),
    subtitle: /*#__PURE__*/React.createElement(PlaylistHeaderSubtitle, {
      tabLinks: tabLinks,
      playlist: playlist,
      showAccessTab: showAccessTab
    }),
    onClose: onClose
  }, hasDetailsTab && /*#__PURE__*/React.createElement(FixedHeightTab, {
    isActive: tabLinks.details.isActive,
    as: Link,
    to: getTabLink('details'),
    "aria-current": tabLinks.details.isActive ? 'page' : undefined,
    isDisabled: playlist === undefined
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "asset-image",
    mr: "xs"
  }), t('panel_tab_details')), hasContentTab && /*#__PURE__*/React.createElement(FixedHeightTab, {
    isActive: tabLinks.content.isActive,
    as: Link,
    to: getTabLink('content'),
    "aria-current": tabLinks.content.isActive ? 'page' : undefined
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "playlist",
    mr: "xs"
  }), t('panel_tab_content')), hasAccessTab && /*#__PURE__*/React.createElement(FixedHeightTab, {
    isActive: tabLinks.access.isActive,
    as: Link,
    to: getTabLink('access'),
    "aria-current": tabLinks.access.isActive ? 'page' : undefined
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "key",
    mr: "xs"
  }), t('panel_tab_access')), hasShareTab && /*#__PURE__*/React.createElement(FixedHeightTab, {
    isActive: tabLinks.share.isActive,
    as: Link,
    to: getTabLink('share'),
    "aria-current": tabLinks.share.isActive ? 'page' : undefined
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "send",
    mr: "xs"
  }), t('panel_tab_share_and_nudge')), hasAnalyticsTab && /*#__PURE__*/React.createElement(FixedHeightTab, {
    isActive: tabLinks.analytics.isActive,
    as: Link,
    to: getTabLink('analytics'),
    "aria-current": tabLinks.analytics.isActive ? 'page' : undefined
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "trending",
    mr: "xs"
  }), t('panel_tab_analytics'))));
}